import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { useBambooSendReceiptMessageMutation } from '@/gql/generated';
import useNetwork from '@/hooks/use-network';
import BackSpaceIcon from '@/icons/back-space-icon';
import { convertBasePhoneNumber, formatNational } from '@/lib/utils';
import { FullScreenLoadingContext } from '@/pages/_providers/full-loading-overlay-provider';
import { zodResolver } from '@hookform/resolvers/zod';
import clsx from 'clsx';
import { AsYouType, isValidPhoneNumber } from 'libphonenumber-js';
import { useContext } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { useHandleToast } from '../../hooks/use-toast';
import TopNavigation from './top-navigation';

const formSchema = z.object({
  phone: z
    .string({
      invalid_type_error: 'invalid_type',
      required_error: 'Phone number is required',
    })
    .min(1, {
      message: 'Phone number is required',
    })
    .refine((value) => isValidPhoneNumber(value, 'US'), {
      message: 'Invalid phone number',
    }),
});

const MessageField = ({ id, onClose }: { id: string; onClose: () => void }) => {
  const { event: eventLoading } = useContext(FullScreenLoadingContext);
  const { showErrorToast, showSuccessToast } = useHandleToast();
  const { isOnline } = useNetwork();

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      phone: '',
    },
  });
  const phoneValue = form.watch('phone');
  const bambooSendReceiptMessageMutation = useBambooSendReceiptMessageMutation({
    onMutate: () => eventLoading.setIsLoading(true),
    onError: () => {
      showErrorToast();
    },
    onSuccess: () => {
      onClose();
      showSuccessToast('The receipt was sent to phone.');
    },
    onSettled: () => {
      eventLoading.setIsLoading(false);
    },
  });

  function onSubmit(values: z.infer<typeof formSchema>) {
    bambooSendReceiptMessageMutation.mutate({
      input_obj: {
        transaction_id: id,
        receiver_phone_number: convertBasePhoneNumber(values.phone) ?? '',
      },
    });
  }
  const NUMERIC_BUTTONS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 'C', 0, 'x'];
  const handleNumberClick = (num: string) => {
    if (phoneValue === '0') {
      form.setValue('phone', num);
    } else {
      const newValue = phoneValue + num;
      form.setValue('phone', newValue);
    }
  };

  const handleClear = () => {
    form.setValue('phone', '');
  };
  const handleDelete = () => {
    if (phoneValue.length === 1) {
      form.setValue('phone', '');
    } else {
      const newValue = phoneValue.slice(0, -1);
      form.setValue('phone', newValue);
    }
  };

  return (
    <div className="fixed z-[200] left-0 top-0 w-full h-full bg-white">
      <TopNavigation onBack={onClose} />

      <div className="flex h-full flex-col w-full">
        <div className="h-full w-full flex items-center justify-center">
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className={clsx(
                form.formState.errors.phone ? 'items-center' : 'items-end',
                'flex justify-center gap-6',
              )}
            >
              <FormField
                control={form.control}
                name="phone"
                render={({ field }) => (
                  <FormItem className="text-center flex flex-col">
                    <FormLabel className="text-5xl font-bold text-gray-primary mb-3">
                      Enter your phone
                    </FormLabel>
                    <div className="flex items-center gap-4">
                      <FormControl>
                        <Input
                          placeholder={formatNational('8888888888')}
                          {...field}
                          className="min-h-24 border-gray-border bg-gray-lightest px-8 py-5 text-5xl font-bold placeholder:text-gray-border"
                          value={new AsYouType('US').input(phoneValue)}
                        />
                      </FormControl>

                      <Button
                        type="submit"
                        className="min-h-24 min-w-48 border border-gray-border text-5xl font-bold disabled:bg-neutral-100 disabled:text-[#b8b8b8]"
                        disabled={!phoneValue || !isOnline}
                      >
                        Send
                      </Button>
                    </div>
                    <FormMessage className="text-xl text-danger-discount" />
                  </FormItem>
                )}
              />
            </form>
          </Form>
        </div>
        <div className="bg-gray-lightest pb-20 flex-none">
          <div className="col-span-5 mx-auto grid w-1/2 grid-cols-3 gap-6 py-6">
            {NUMERIC_BUTTONS.map((num, i) => (
              <Button
                key={i}
                variant="outline"
                className="flex h-24 items-center justify-center rounded-lg border border-gray-border bg-gray-lightest text-5xl font-bold text-gray-700 hover:bg-gray-50"
                onClick={() => {
                  if (num === 'C') {
                    handleClear();
                    return;
                  }
                  if (num === 'x') {
                    handleDelete();
                    return;
                  }
                  handleNumberClick(num.toString());
                }}
              >
                {num === 'x' ? (
                  <BackSpaceIcon
                    style={{
                      width: 48,
                      height: 48,
                    }}
                  />
                ) : (
                  num
                )}
              </Button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MessageField;
